import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, sortBy, minBy } from 'lodash-es';
import Label from 'reactstrap/lib/Label';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
/* Models */
import { VehicleModel, VehicleEntities, buildVehiclePath, buildPopularStylesPath } from 'client/data/models/vehicle-v2';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
/* Utils */
import { getUrlPathFromParams } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';
import { TrackingHandler } from 'client/tracking/handler';
import { getQuery } from 'client/utils/location';
import { isNew as isNewPubState } from 'site-modules/shared/utils/publication-states';
import { IntersectionObserver } from 'client/utils/intersection-observer';
import { getStyleName } from 'site-modules/shared/utils/features-specs/utils';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
/* Components */
import { CoreBreadcrumbs } from 'site-modules/shared/components/core-breadcrumbs/core-breadcrumbs';
import { ModelOverview } from 'client/site-modules/incentives/components/model-overview/model-overview';
import { MrectNative } from 'client/site-modules/shared/components/native-ad/mrect/mrect-native';
import { IncentivesPanel } from 'client/site-modules/incentives/components/incentives-panel/incentives-panel';
import { ShoppingTools } from 'client/site-modules/incentives/components/shopping-tools/shopping-tools';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { StyledZipInput } from 'site-modules/shared/components/styled-zip-input/styled-zip-input';
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { StyledSelect } from 'client/site-modules/shared/components/styled-select/styled-select';
import { BuildPriceAd } from 'site-modules/shared/components/native-ad/buildprice-ad/buildprice-ad';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import { IncentivesAd } from 'site-modules/shared/components/native-ad/incentives-ad/incentives-ad';
import { SponsoredCars } from 'client/site-modules/shared/components/core-page/sponsored-cars/sponsored-cars';
import { MinHeightWrapper } from 'site-modules/shared/components/min-height-wrapper/min-height-wrapper';
import { StylesIncentivesMobile } from 'site-modules/incentives/components/styles-incentives/styles-incentives-mobile';
import { StylesIncentives } from 'site-modules/incentives/components/styles-incentives/styles-incentives';
import { SatisfactionWidget } from 'site-modules/shared/components/satisfaction-widget/satisfaction-widget';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { FancyLabel } from 'site-modules/shared/components/custom-dropdown-label/custom-dropdown-label';
/* Constants */
import { TrackingMap } from 'client/tracking/maps/incentives/index';
import { MRECT_WIRED_CUSTOM_SIZE } from 'site-modules/shared/components/ad-unit/context/sizes';

import './incentives-model.scss';

export const PAGE_DISPLAY_NAME = 'Incentives and Rebates';
export const URL_PATH = 'deals';

class IncentivesModelUI extends Component {
  static propTypes = {
    makeModelSubmodelYear: VehicleEntities.MakeModelSubmodelYear,
    location: PropTypes.shape({}),
    styles: VehicleEntities.Styles,
    isMobile: PropTypes.bool,
    pageNameLegacy: PropTypes.string,
    params: PropTypes.shape({}),
  };

  static defaultProps = {
    makeModelSubmodelYear: null,
    location: null,
    styles: null,
    isMobile: false,
    pageNameLegacy: '',
    params: {},
  };

  constructor(props) {
    super(props);

    const { defaultSection } = getQuery(props.location);

    this.state = {
      style: undefined,
      selectedIncentiveType: defaultSection,
      shouldRenderWidget: false,
      isObserved: false,
    };

    this.ref = React.createRef();
    this.incentivesWrapper = React.createRef();

    this.observer = IntersectionObserver && new IntersectionObserver(this.handleWidgetInView);
  }

  componentDidMount() {
    TrackingHandler.useMap(TrackingMap);

    if (this.props.makeModelSubmodelYear && this.observer && this.ref.current) {
      this.observer.observe(this.ref.current);
    }
  }

  componentDidUpdate(prevProps) {
    const { makeModelSubmodelYear: prevMakeModelSubmodelYear } = prevProps;
    const { makeModelSubmodelYear } = this.props;

    if (makeModelSubmodelYear && !prevMakeModelSubmodelYear && this.observer && this.ref.current) {
      this.observer.observe(this.ref.current);
    }
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /**
   * Sets selected style.
   * @param {object} style
   */
  setStyle = style => {
    this.setState(() => ({ style, selectedIncentiveType: '' }));
  };

  handleWidgetInView = entries => {
    entries.forEach(entry => {
      const { isObserved } = this.state;

      if (entry.isIntersecting && !isObserved) {
        this.setState({ isObserved: true });
      }
      if (!entry.isIntersecting && isObserved) {
        this.setState({ shouldRenderWidget: true });
      }
    });
  };

  /**
   * Handles click on incentive.
   * @param {object} style
   * @param {string} incentiveType
   */
  handleIncentiveClick = (style, incentiveType) => {
    this.setState(() => ({
      style,
      selectedIncentiveType: incentiveType,
    }));
  };

  render() {
    const { makeModelSubmodelYear, location, styles, isMobile, pageNameLegacy } = this.props;
    const { defaultStyleId } = getQuery(location);

    if (!styles) {
      return null;
    }

    const { style, selectedIncentiveType, shouldRenderWidget } = this.state;
    const makeName = get(makeModelSubmodelYear, 'make.name', '');
    const modelName = get(makeModelSubmodelYear, 'model.name', '');
    const makeSlug = get(makeModelSubmodelYear, 'make.slug', '');
    const modelSlug = get(makeModelSubmodelYear, 'model.slug', '');
    const year = get(makeModelSubmodelYear, 'year', '');
    const pubStates = get(makeModelSubmodelYear, 'pubStates', '');

    const isNew = isNewPubState(pubStates);

    const srpParams = {
      make: makeSlug,
      model: modelSlug,
      year: Number(year),
    };
    const srpLink = srpLinkBuilder(srpParams);
    // Get object for selected style by user, defaultStyle from query params or the first style from styles
    const defaultStyle = styles.find(({ id }) => parseInt(defaultStyleId, 10) === id);
    const selectedStyle = style || defaultStyle || minBy(styles, 'price.baseMSRP') || styles[0];
    const { style: styleName, engine: engineName } = getStyleName(selectedStyle.name);

    const vehicleName = `${year} ${makeName} ${modelName}`;
    const StylesIncentivesComponent = isMobile ? StylesIncentivesMobile : StylesIncentives;
    const headerText = `${vehicleName} Deals, Incentives & Rebates`;

    return (
      <Container className="incentives-model" id="main-content" tabIndex={-1}>
        <Row>
          <Col xs={12} lg={8} tag="main">
            <CoreBreadcrumbs
              className="ps-0"
              vehicle={makeModelSubmodelYear}
              breadcrumbsParams={{
                params: srpParams,
                pageDisplayName: PAGE_DISPLAY_NAME,
                urlPath: getUrlPathFromParams(srpParams, URL_PATH),
              }}
            />
            <Row className="my-1_5" tag="section" data-tracking-parent="edw-incentive-model-information">
              <Col md={8}>
                <h1 className="heading-2 mb-1_5">{headerText}</h1>
              </Col>
              <Col xs={12} md={4} className="px-0_5 order-md-first">
                <ModelOverview vehicle={makeModelSubmodelYear} />
              </Col>
            </Row>

            <Row
              tag={ScrollElement}
              data-tracking-parent="edw-incentive-model-payment-calculator"
              className="mb-2 align-items-center align-items-md-end"
              id="incentives-rebates-calculator"
            >
              <Col xs={12} sm={6} className="mb-1_5 mb-md-0">
                <Label for="style-custom-select" className="fw-bold mb-0_75">
                  Select a trim:
                </Label>
                <div className="pos-r">
                  <StyledSelect
                    size="lg"
                    value={selectedStyle && selectedStyle.id.toString()}
                    onChange={this.setStyle}
                    options={sortBy(styles, 'price.baseMSRP')}
                    id="style-custom-select"
                    name="select-style"
                    defaultValue={0}
                    valueKey="id"
                    labelKey="label"
                    wrapperClassName="style-custom-select"
                    className="form-control-lg form-control text-start text-gray-darker ps-1 pe-2_5"
                    data-tracking-id="nav_mmy_model_select"
                    style={{ height: '4.5rem', maxHeight: '4.5rem' }}
                  />
                  <div
                    role="presentation"
                    className="pos-a text-truncate bg-white"
                    style={{
                      pointerEvents: 'none',
                      left: '1rem',
                      right: '2.5rem',
                      bottom: '0.5rem',
                    }}
                  >
                    <FancyLabel
                      primaryText={styleName}
                      secondaryText={engineName}
                      primaryTextClasses="heading-5"
                      secondaryTextClasses="medium"
                      showHighlightText={selectedStyle.id === styles[0].id}
                    />
                  </div>
                </div>
              </Col>
              <Col xs={12} md={3}>
                <StyledZipInput
                  inputClasses="search-by-zip-input size-16"
                  onValidate={validation.validateZip}
                  size="lg"
                  getTracking={{ 'data-tracking-id': 'provide_zip_code' }}
                />
              </Col>
            </Row>

            <div ref={this.incentivesWrapper}>
              <IncentivesPanel
                selectedStyle={selectedStyle}
                selectedIncentiveType={selectedIncentiveType}
                makeModelSubmodelYear={makeModelSubmodelYear}
                isMobile={isMobile}
                pageNameLegacy={pageNameLegacy}
              />
              <IncentivesAd wrapperClass="my-2" mobile={isMobile} />
              <StylesIncentivesComponent
                styles={styles}
                mmy={vehicleName}
                link={srpLink}
                onIncentiveClick={this.handleIncentiveClick}
              />
              <BuildPriceAd mobile={isMobile} wrapperClass="build-price" includeMrectAdCall={isNew} />
              {isMobile && (
                <Fragment>
                  <ShoppingTools makeModelSubmodelYear={makeModelSubmodelYear} />
                  <hr className="mt-0_75 mb-2" />
                </Fragment>
              )}
            </div>
          </Col>
          <Col xs={12} lg={4} tag="aside" className="pt-lg-3">
            {!isMobile && (
              <>
                <div>
                  <ShoppingTools makeModelSubmodelYear={makeModelSubmodelYear} />
                  <hr className="mt-0_75" />
                </div>

                <MrectNative position={'1'} refreshWhenViewable={false} refreshable={false} useMinHeight />
                <MrectNative
                  wrapperClass="my-2"
                  position={'2'}
                  refreshWhenViewable={false}
                  refreshable={false}
                  useMinHeight
                  addCustomSizesWired={MRECT_WIRED_CUSTOM_SIZE}
                />
              </>
            )}
          </Col>
        </Row>

        <Experiment name="ads-12460_dfp" showDefault>
          <Recipe name="ctrl" isDefault>
            <MinHeightWrapper componentType="SPONSORED_CARS" className="mt-2 mt-md-0">
              <SponsoredCars />
            </MinHeightWrapper>
          </Recipe>
          <Recipe name="chal">
            <MinHeightWrapper
              componentType="SPONSORED_CARS_STACKED"
              className="mt-2 mt-md-0"
              style={isMobile ? null : { maxWidth: '744px' }}
            >
              <SponsoredCars showStackedAds />
            </MinHeightWrapper>
          </Recipe>
        </Experiment>
        <span ref={this.ref} />
        {shouldRenderWidget && <SatisfactionWidget isFeedbackEnabled />}
        {isNew && <PageLinksWidget divideSections={false} title={`Related ${vehicleName} info`} />}
      </Container>
    );
  }
}

const IncentivesModelWrapper = connectToModel(IncentivesModelUI, {
  styles: bindToPath(
    ({ params, makeModelSubmodelYear }) => {
      const { make, model, year } = params;
      return makeModelSubmodelYear && make && model && year && buildPopularStylesPath(params);
    },
    VehicleModel,
    styles =>
      styles.map((style, index) => ({
        ...style,
        label: `${style.name} - ${getPriceString(get(style, 'price.baseMSRP'))}${index ? '' : ' (Most Popular)'}`,
      }))
  ),
});

const stateToPropsConfig = {
  makeModelSubmodelYear: bindToPath(({ params }) => buildVehiclePath(params), VehicleModel),
};

const mapStateToProps = state => ({
  isMobile: get(state, 'mobile'),
  location: get(state, 'pageContext.location'),
  pageNameLegacy: get(state, 'pageContext.legacy.pageName') || get(state, 'pageContext.page.name'),
});

export const IncentivesModel = connect(mapStateToProps)(connectToModel(IncentivesModelWrapper, stateToPropsConfig));
